import { css } from '@styled-system/css';
import { Search } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button';
import { ChipToggle } from '@/shared/components/ui/ChipToggle';
import { Input } from '@/shared/components/ui/Input';
import type { SelectOption } from '@/shared/types/options';

const chipPickerWrap = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '4',
});

const chipGrid = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '2',
});

const chipPickerHint = css({
  fontSize: 'caption',
  color: 'text.tertiary',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
});

// ============================================================================
// ChipMultiPicker — search + select all/clear + grid of toggleable chips
// ============================================================================

export interface ChipMultiPickerProps {
  options: SelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  searchPlaceholder?: string;
  emptyHint?: string;
  /** Optional resolver: returns hex color for a chip dot. Used for color filters. */
  dotFor?: (option: SelectOption) => string | undefined;
  /** Show the search bar even with few options. Defaults to auto (only if >8 options). */
  forceSearch?: boolean;
}

export const ChipMultiPicker: FC<ChipMultiPickerProps> = ({
  options,
  value,
  onChange,
  searchPlaceholder,
  emptyHint,
  dotFor,
  forceSearch,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (val: string) => {
    if (value.includes(val)) onChange(value.filter((v) => v !== val));
    else onChange([...value, val]);
  };

  const showSearch = forceSearch || options.length > 8;
  const hasValues = value.length > 0;

  return (
    <div className={chipPickerWrap}>
      {showSearch && (
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder ?? t('segment.searchPlaceholder')}
          leading={<Search size={14} strokeWidth={1.75} />}
        />
      )}
      <div className={chipGrid}>
        {filtered.map((opt) => (
          <ChipToggle
            key={opt.value}
            selected={value.includes(opt.value)}
            onChange={() => toggle(opt.value)}
            dot={dotFor?.(opt)}
          >
            {opt.label}
          </ChipToggle>
        ))}
        {filtered.length === 0 && (
          <span className={css({ fontSize: 'caption', color: 'text.tertiary', fontStyle: 'italic', paddingY: '3' })}>
            {t('searchModal.noResults')}
          </span>
        )}
      </div>
      {emptyHint && <span className={chipPickerHint}>ⓘ {emptyHint}</span>}
      {hasValues && (
        <div className={css({ display: 'flex', justifyContent: 'flex-end', marginTop: '1' })}>
          <Button variant="ghost" size="sm" onClick={() => onChange([])}>
            {t('searchModal.clearSelection')}
          </Button>
        </div>
      )}
    </div>
  );
};
